import { createSentryBreadcrumb } from "@/helpers/sentry";
import { CUSTOM_IFRAME_EVENT } from "@/constants/iframe";

/** Subroutes mapping of the embedded checkout documentation on https://docs.esenda.com/docs/embedded-checkout */
type EmbeddedCheckoutDocsPage =
    | "domain-setup"
    | "checkout-session"
    | "iframe-setup"
    | "iframe-customisation"
    | "webhooks"
    | "status"
    | "flow-events"
    | "theme-validator";

type EmbeddedCheckoutProblem = {
    checkId: string;
    level: "error" | "warning";
    message: string;
    docsPage: EmbeddedCheckoutDocsPage;
};

const loggedChecks = new Set<string>();

/**
 * Reports integration problems only inside an iframe, once per check per page load.
 *
 * Use a stable, unique checkId for each check, not each message or component
 * instance. Deduplication survives component remounts and internal navigation;
 * reloading the iframe resets the in-memory state.
 *
 * Use "error" when a required payment permission or listed payment method is blocked, and
 * "warning" for a missing but non-blocking integration requirement.
 *
 * Messages start with "[Esenda Checkout]" and end with an existing
 * embedded-checkout page on https://docs.esenda.com. Each report also attempts
 * to add a "checkout.integration" Sentry breadcrumb at the same severity.
 * Also posts { type: "INTEGRATION_DIAGNOSTIC", checkId, level, message, docsUrl }
 * to window.parent, using "*" like the existing checkout flow events because
 * the embedding origin may be unavailable. The payload contains diagnostics only.
 * Parent listeners should validate event.origin and event.source before using it.
 * All three outputs are attempted independently; failures cannot interrupt
 * checkout and are not retried.
 * Never include payer information or session secrets in diagnostic messages.
 *
 * @param problem - Integration diagnostic to report.
 * @param problem.checkId - Stable identifier used for page-load deduplication.
 * @param problem.level - "error" for blocked payment requirements; otherwise "warning".
 * @param problem.message - Partner-facing explanation without sensitive data.
 * @param problem.docsPage - Existing embedded-checkout documentation subroute.
 *
 * @example
 * logEmbeddedCheckoutProblem({
 *     checkId: "iframe-payment-permission",
 *     level: "error",
 *     message: "Apple Pay cannot work because payment permission is missing.",
 *     docsPage: "iframe-setup",
 * });
 */
export const logEmbeddedCheckoutProblem = (
    problem: EmbeddedCheckoutProblem,
): void => {
    try {
        if (
            typeof window === "undefined" ||
            window.self === window.top ||
            loggedChecks.has(problem.checkId)
        ) {
            return;
        }

        loggedChecks.add(problem.checkId);
        const docsUrl = `https://docs.esenda.com/docs/embedded-checkout/${problem.docsPage}`;
        const message = `[Esenda Checkout] ${problem.message} ${docsUrl}`;

        try {
            if (problem.level === "error") {
                console.error(message);
            } else {
                console.warn(message);
            }
        } finally {
            try {
                createSentryBreadcrumb(
                    "checkout.integration",
                    message,
                    problem.level,
                );
            } finally {
                window.parent.postMessage(
                    {
                        type: CUSTOM_IFRAME_EVENT.INTEGRATION_DIAGNOSTIC,
                        checkId: problem.checkId,
                        level: problem.level,
                        message,
                        docsUrl,
                    },
                    "*",
                );
            }
        }
    } catch {
        // Suppress logger failures so they cannot interrupt checkout. The finally
        // blocks attempt Sentry and parent delivery even if another output throws.
        // Do not call this logger
        // again from here: reporting its own failure could cause infinite recursion.
    }
};
